import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import FormHelperText from '@material-ui/core/FormHelperText';
import Menu from '@material-ui/core/Menu';
import Tooltip from '@material-ui/core/Tooltip';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

const HIGHLIGHT = '#1976d2';

const ROW_BASE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '2px 0 2px 0',
  fontSize: '16px',
  lineHeight: 1.4,
  cursor: 'pointer',
  whiteSpace: 'nowrap'
};

const SQUARE_PAPER = {
  square: true,
  elevation: 0,
  style: { border: '1px solid rgba(0, 0, 0, 0.23)' }
};

function LeafRow({ label, onClick }) {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      style={{ ...ROW_BASE, ...(hover ? { backgroundColor: HIGHLIGHT, color: '#fff' } : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') onClick(e);
      }}
    >
      {label}
    </div>
  );
}
LeafRow.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func.isRequired };

/**
 * Recursively walks the content tree to find the full label path (breadcrumb)
 * belonging to a stored value. Returns e.g. "Buchung Paket Professional › Versand
 * Rechnung" so the field unambiguously shows the selection - the stored value
 * itself is a unique, path-encoded key (e.g. "buchung.professional.versand_rechnung")
 */
function findPathForValue(items, value) {
  if (!Array.isArray(items)) return null;
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (item.submenu && item.submenu.length > 0) {
      const childPath = findPathForValue(item.submenu, value);
      if (childPath) return [item.label, ...childPath];
    } else if (item.value === value) {
      return [item.label];
    }
  }
  return null;
}

/**
 * A MenuItem that opens a nested Menu on hover.
 */
const NestedMenuItem = React.forwardRef((props, ref) => {
  const { label, items, parentMenuOpen, onSelect } = props;
  const [isSubMenuOpen, setIsSubMenuOpen] = React.useState(false);
  const menuItemRef = React.useRef(null);

  const open = isSubMenuOpen && parentMenuOpen;

  return (
    <div ref={ref} onMouseEnter={() => setIsSubMenuOpen(true)} onMouseLeave={() => setIsSubMenuOpen(false)}>
      <div ref={menuItemRef} style={{ ...ROW_BASE, ...(isSubMenuOpen ? { backgroundColor: HIGHLIGHT, color: '#fff' } : {}) }}>
        <span>{label}</span>
        <ArrowRightIcon fontSize="small" style={{ marginLeft: 16, color: isSubMenuOpen ? '#fff' : undefined }} />
      </div>
      <Menu
        style={{ pointerEvents: 'none' }}
        anchorEl={menuItemRef.current}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={open}
        autoFocus={false}
        disableAutoFocusItem
        disableAutoFocus
        disableEnforceFocus
        onClose={() => setIsSubMenuOpen(false)}
        PaperProps={SQUARE_PAPER}
        MenuListProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
      >
        {/* eslint-disable-next-line no-use-before-define */}
        <div style={{ pointerEvents: 'auto' }}>{renderMenuItems(items, open, onSelect)}</div>
      </Menu>
    </div>
  );
});

NestedMenuItem.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  parentMenuOpen: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired
};

function renderMenuItems(items, parentMenuOpen, onSelect) {
  return items.map(item =>
    item.submenu && item.submenu.length > 0 ? (
      <NestedMenuItem key={item.label} label={item.label} items={item.submenu} parentMenuOpen={parentMenuOpen} onSelect={onSelect} />
    ) : (
      <LeafRow key={item.value} label={item.label} onClick={() => onSelect(item.value, item.label)} />
    )
  );
}

class ValidatedNestedDropDown extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      isValid: true,
      errorText: ''
    };
  }

  // called by GenericForm via ref on submit
  isInputValid = () => {
    let isValid = true;
    let errorText = '';
    if (this.props.required && (!this.props.value || this.props.value.length === 0)) {
      errorText = this.props.errorMessage;
      isValid = false;
    }
    this.setState({ isValid, errorText });
    return isValid;
  };

  openMenu = e => this.setState({ anchorEl: e.currentTarget });

  closeMenu = () => this.setState({ anchorEl: null });

  handleSelect = value => {
    this.closeMenu();
    this.props.onChange({ target: { name: this.props.id, value } });
    this.isInputValid();
  };

  render() {
    const { anchorEl } = this.state;
    const open = Boolean(anchorEl);
    const selectedPath = findPathForValue(this.props.content, this.props.value);
    const selectedLabel = selectedPath ? selectedPath.join(' › ') : '';

    return (
      <FormControl margin={this.props.margin} error={!this.state.isValid} fullWidth>
        {this.props.label && (
          <InputLabel htmlFor={this.props.id} shrink>
            {this.props.required ? `${this.props.label}*` : this.props.label}
          </InputLabel>
        )}
        <Tooltip title={selectedLabel} placement="top-start">
          <Input
            id={this.props.id}
            readOnly
            value={selectedLabel}
            onClick={this.openMenu}
            style={{ cursor: 'pointer' }}
            endAdornment={<ArrowDropDownIcon style={{ pointerEvents: 'none', color: 'rgba(0, 0, 0, 0.54)' }} />}
            inputProps={{ style: { cursor: 'pointer' } }}
          />
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={this.closeMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={SQUARE_PAPER}
          MenuListProps={{ style: { paddingTop: 0, paddingBottom: 0 } }}
        >
          {/* empty first entry to clear the selection */}
          <LeafRow key="__empty__" label=" " onClick={() => this.handleSelect('')} />
          {renderMenuItems(this.props.content, open, this.handleSelect)}
        </Menu>
        {(this.props.errorText || !this.state.isValid) && <FormHelperText>{this.props.errorText || this.state.errorText}</FormHelperText>}
      </FormControl>
    );
  }
}

ValidatedNestedDropDown.defaultProps = {
  errorMessage: 'Bitte treffen Sie eine Auswahl',
  required: false,
  margin: 'none',
  value: '',
  errorText: undefined
};

ValidatedNestedDropDown.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  content: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  errorText: PropTypes.string,
  required: PropTypes.bool,
  margin: PropTypes.string
};

export default ValidatedNestedDropDown;
